import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";

export function lerpPoint(from: { x: number; y: number }, to: { x: number; y: number }, t: number): { x: number; y: number } {
  return {
    x: from.x + (to.x - from.x) * t,
    y: from.y + (to.y - from.y) * t,
  };
}

/**
 * De Casteljau subdivision of a vector edge at parameter `t`.
 * Both handles must exist for the cubic path; otherwise the edge is treated as a
 * straight segment and the new vertex lands at `fallbackPoint`.
 */
export function splitEdge(
  edge: VectorEdge,
  network: Pick<VectorNetwork, "vertices">,
  vertexId: string,
  firstEdgeId: string,
  secondEdgeId: string,
  t: number,
  fallbackPoint: { x: number; y: number },
): {
  vertex: VectorVertex;
  firstEdge: VectorEdge;
  secondEdge: VectorEdge;
} {
  const from = network.vertices.find(vertex => vertex.id === edge.from.vertexId);
  const to = network.vertices.find(vertex => vertex.id === edge.to.vertexId);
  if (!from || !to || !edge.from.handle || !edge.to.handle) {
    return {
      vertex: { id: vertexId, x: fallbackPoint.x, y: fallbackPoint.y },
      firstEdge: { id: firstEdgeId, from: { vertexId: edge.from.vertexId }, to: { vertexId }, directed: edge.directed },
      secondEdge: { id: secondEdgeId, from: { vertexId }, to: { vertexId: edge.to.vertexId }, directed: edge.directed },
    };
  }

  const p01 = lerpPoint(from, edge.from.handle, t);
  const p12 = lerpPoint(edge.from.handle, edge.to.handle, t);
  const p23 = lerpPoint(edge.to.handle, to, t);
  const p012 = lerpPoint(p01, p12, t);
  const p123 = lerpPoint(p12, p23, t);
  const p0123 = lerpPoint(p012, p123, t);

  return {
    vertex: { id: vertexId, x: p0123.x, y: p0123.y },
    firstEdge: {
      id: firstEdgeId,
      from: { vertexId: edge.from.vertexId, handle: p01 },
      to: { vertexId, handle: p012 },
      directed: edge.directed,
    },
    secondEdge: {
      id: secondEdgeId,
      from: { vertexId, handle: p123 },
      to: { vertexId: edge.to.vertexId, handle: p23 },
      directed: edge.directed,
    },
  };
}

export function splitEdgeDisconnected(
  edge: VectorEdge,
  network: Pick<VectorNetwork, "vertices">,
  cut: {
    point: { x: number; y: number };
    t: number;
    fromVertexId: string;
    toVertexId: string;
    firstEdgeId: string;
    secondEdgeId: string;
  },
): {
  fromVertex: VectorVertex;
  toVertex: VectorVertex;
  firstEdge: VectorEdge;
  secondEdge: VectorEdge;
} {
  const from = network.vertices.find(vertex => vertex.id === edge.from.vertexId);
  const to = network.vertices.find(vertex => vertex.id === edge.to.vertexId);
  if (!from || !to || !edge.from.handle || !edge.to.handle) {
    return {
      fromVertex: { id: cut.fromVertexId, x: cut.point.x, y: cut.point.y },
      toVertex: { id: cut.toVertexId, x: cut.point.x, y: cut.point.y },
      firstEdge: { id: cut.firstEdgeId, from: { vertexId: edge.from.vertexId }, to: { vertexId: cut.fromVertexId }, directed: edge.directed },
      secondEdge: { id: cut.secondEdgeId, from: { vertexId: cut.toVertexId }, to: { vertexId: edge.to.vertexId }, directed: edge.directed },
    };
  }

  const split = splitEdge(edge, network, cut.fromVertexId, cut.firstEdgeId, cut.secondEdgeId, cut.t, cut.point);
  return {
    fromVertex: split.vertex,
    toVertex: { ...split.vertex, id: cut.toVertexId },
    firstEdge: split.firstEdge,
    secondEdge: { ...split.secondEdge, from: { ...split.secondEdge.from, vertexId: cut.toVertexId } },
  };
}
